import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "serviceCard",
    "servicesList",
    "selectedServices",
    "poolSize",
    "subtotal",
    "total",
    "checkbox",
    "upsellCard",
    "upsellCheckbox",
    "customItemsList",
    "customItemTemplate",
    "customItem"
  ]

  connect() {
    this.recalculateTotal()
  }

  addCustomItem() {
    if (!this.hasCustomItemTemplateTarget || !this.hasCustomItemsListTarget) return

    const template = this.customItemTemplateTarget
    const clone = template.content.cloneNode(true)
    this.customItemsListTarget.appendChild(clone)

    // Focus the description input
    const lastItem = this.customItemsListTarget.lastElementChild
    const descInput = lastItem?.querySelector('input[type="text"]')
    if (descInput) descInput.focus()
  }

  removeCustomItem(event) {
    const item = event.target.closest('[data-manual-order-target="customItem"]')
    if (item) {
      item.remove()
      this.recalculateTotal()
    }
  }

  toggleService(event) {
    // Don't toggle if clicking directly on checkbox (it handles itself)
    if (event.target.type === 'checkbox') {
      this.updateCardStyles(event.target, 'serviceCard')
      this.recalculateTotal()
      return
    }

    // Find the card and its checkbox
    const card = event.currentTarget
    const checkbox = card.querySelector('input[type="checkbox"]')

    if (checkbox) {
      checkbox.checked = !checkbox.checked
      this.updateCardStyles(checkbox, 'serviceCard')
      this.recalculateTotal()
    }
  }

  toggleUpsell(event) {
    const checkbox = event.target
    this.updateCardStyles(checkbox, 'upsellCard')
    this.recalculateTotal()
  }

  updateCardStyles(checkbox, cardType) {
    const card = checkbox.closest(`[data-manual-order-target="${cardType}"]`)
    if (!card) return

    // Toggle selected class (styles defined in CSS .selectable-card.selected)
    card.classList.toggle('selected', checkbox.checked)
  }

  recalculateTotal() {
    const poolSize = this.hasPoolSizeTarget ? parseFloat(this.poolSizeTarget.value) || 0 : 0
    let servicesTotal = 0
    let upsellsTotal = 0
    let customItemsTotal = 0
    const selectedServices = []
    const selectedUpsells = []
    const customItems = []

    // Calculate services total
    this.serviceCardTargets.forEach(card => {
      const checkbox = card.querySelector('input[type="checkbox"]')
      if (checkbox && checkbox.checked) {
        const basePrice = parseInt(card.dataset.basePrice) || 0
        const pricePerSqft = parseInt(card.dataset.pricePerSqft) || 0
        const pricingModel = card.dataset.pricingModel

        let servicePrice = basePrice
        if (pricingModel === 'per_sqft' && poolSize > 0) {
          servicePrice = basePrice + (poolSize * pricePerSqft)
        }

        // Update individual service price display
        const priceDisplay = card.querySelector('[data-manual-order-target="servicePrice"]')
        if (priceDisplay) {
          priceDisplay.textContent = this.formatCurrency(servicePrice / 100)
        }

        servicesTotal += servicePrice

        // Get service name for summary (look for h3 or font-semibold)
        const serviceName = card.querySelector('h3')?.textContent ||
                           card.querySelector('.font-semibold')?.textContent ||
                           'Service'
        selectedServices.push({
          name: serviceName.trim(),
          price: servicePrice / 100
        })
      }
    })

    // Calculate upsells total
    this.upsellCardTargets.forEach(card => {
      const checkbox = card.querySelector('input[type="checkbox"]')
      if (checkbox && checkbox.checked) {
        const price = parseInt(card.dataset.price) || 0
        upsellsTotal += price

        const upsellName = card.querySelector('h3')?.textContent ||
                          card.querySelector('.font-semibold')?.textContent ||
                          'Add-on'
        selectedUpsells.push({
          name: upsellName.trim(),
          price: price / 100
        })
      }
    })

    // Calculate custom items total
    this.customItemTargets.forEach(item => {
      const descInput = item.querySelector('input[type="text"]')
      const amountInput = item.querySelector('input[type="number"]')

      const description = descInput?.value?.trim() || ''
      const amount = parseFloat(amountInput?.value) || 0

      if (amount > 0) {
        customItemsTotal += Math.round(amount * 100) // Convert to cents
        customItems.push({
          name: description || 'Custom item',
          price: amount
        })
      }
    })

    const total = servicesTotal + upsellsTotal + customItemsTotal

    // Update totals
    if (this.hasSubtotalTarget) {
      this.subtotalTarget.textContent = this.formatCurrency(total / 100)
    }
    if (this.hasTotalTarget) {
      this.totalTarget.textContent = this.formatCurrency(total / 100)
    }

    // Update selected services list
    this.updateSelectedServicesList(selectedServices, selectedUpsells, customItems)
  }

  updateSelectedServicesList(services, upsells, customItems = []) {
    if (!this.hasSelectedServicesTarget) return

    if (services.length === 0 && upsells.length === 0 && customItems.length === 0) {
      this.selectedServicesTarget.innerHTML = '<p class="text-sm text-gray-500 dark:text-gray-400">No services selected</p>'
      return
    }

    let html = ''

    // Services
    if (services.length > 0) {
      html += services.map(item => `
        <div class="flex justify-between text-sm">
          <span class="text-gray-700 dark:text-gray-300">${item.name}</span>
          <span class="text-gray-900 dark:text-white font-medium">${this.formatCurrency(item.price)}</span>
        </div>
      `).join('')
    }

    // Upsells (with different styling to differentiate)
    if (upsells.length > 0) {
      if (services.length > 0) {
        html += '<div class="border-t border-gray-100 dark:border-gray-700 my-2"></div>'
        html += '<p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Add-ons</p>'
      }
      html += upsells.map(item => `
        <div class="flex justify-between text-sm">
          <span class="text-gray-600 dark:text-gray-400">${item.name}</span>
          <span class="text-gray-700 dark:text-gray-300 font-medium">${this.formatCurrency(item.price)}</span>
        </div>
      `).join('')
    }

    // Custom items
    if (customItems.length > 0) {
      if (services.length > 0 || upsells.length > 0) {
        html += '<div class="border-t border-gray-100 dark:border-gray-700 my-2"></div>'
        html += '<p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Custom</p>'
      }
      html += customItems.map(item => `
        <div class="flex justify-between text-sm">
          <span class="text-gray-600 dark:text-gray-400">${item.name}</span>
          <span class="text-gray-700 dark:text-gray-300 font-medium">${this.formatCurrency(item.price)}</span>
        </div>
      `).join('')
    }

    this.selectedServicesTarget.innerHTML = html
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }
}
